import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { css } from '@emotion/css';
import { isEmpty } from 'lodash';

import { Maybe } from 'models/types';
import { EMPTY_STRING } from 'common/utils/string';
import * as LayoutDataContext from 'utils/LayoutDataContext.bs';
import { DrainContext } from 'contexts/DrainContext';
import { FilterTaxonomy } from 'api/queries/filter';

import { Color } from 'css/theme';

/**
 * Numeric search activity
 * @param value
 */
function alphaNumericSearch(value: string): string {
  return value.replace(/[^\w\s]/g, '$&');
}

const DEFAULT_SEARCH_PLACEHOLDER = 'Search Brands';

const classes = {
  root: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `,
  icon: css`
    padding: 20px;
    position: absolute;
    right: 0;
    top: -9px;
    color: ${Color.Gray800};
  `,
  placeholder: css`
    ::placeholder {
      color: ${Color.Gray800};
    }
  `,
  input: css`
    border: 1px solid ${Color.Purple400};
  `,
};

type Props = {
  setSearch: any;
  defaultValue: string;
  className: string;
  placeholder?: Maybe<string>;
  trackSearchReset?: Maybe<any>;
  filterList?: FilterTaxonomy;
};

export const SearchInput: React.FC<Props> = ({
  setSearch,
  defaultValue,
  className,
  placeholder,
  trackSearchReset,
  filterList,
}) => {
  const drain = useContext(DrainContext);

  const layoutData = LayoutDataContext.useContext();

  const [searchTerm, setSearchTerm] = useState<string>(defaultValue);

  useEffect(() => {
    drain.setSearch(searchTerm);
  }, [searchTerm]);

  const configurablePlaceholder = useMemo(() => {
    return layoutData.searchBarPlaceholder.length > 0 ? layoutData.searchBarPlaceholder : placeholder || placeholder;
  }, [layoutData.searchBarPlaceholder, placeholder]);

  return (
    <div className={classNames([className, classes.root])}>
      <input
        name="search"
        value={searchTerm}
        placeholder={
          !isEmpty(configurablePlaceholder)
            ? configurablePlaceholder
            : filterList.search.title || DEFAULT_SEARCH_PLACEHOLDER
        }
        className={classNames([
          'text-base bg-white h-12 pr-12 pl-6 lg:pl-6 sm:pl-6 outline-none focus:outline-none focus:shadow-sm w-full rounded-full',
          classes.placeholder,
          classes.input,
          'search-input',
        ])}
        onChange={event => {
          const value: string = event.target.value;
          const cleanValue = alphaNumericSearch(value);
          drain.trackDrainFilterSection(drain.filter, cleanValue);
          drain.onSearchChange(true);
          setSearchTerm(value);
          setSearch(cleanValue);
        }}
      />
      <div className={classes.icon}>
        {searchTerm.length > 0 ? (
          <FontAwesomeIcon
            icon={['far', 'times']}
            className={classNames({
              'cursor-pointer': searchTerm.length > 0,
              'clear-button': true,
            })}
            onClick={() => {
              if (searchTerm.length > 0) {
                setSearchTerm(EMPTY_STRING);
                drain.setSearch(EMPTY_STRING);
                drain.trackDrainFilterSection(drain.filter, EMPTY_STRING);
                trackSearchReset();
              }
            }}
          />
        ) : (
          <FontAwesomeIcon icon={['far', 'search']} />
        )}
      </div>
    </div>
  );
};
